/**
 * Wraps a reporting operation so it never blocks or throws.
 *
 * Bulk-action hooks record per-item progress to the reporting store,
 * but those writes are purely informational — they must never block
 * or break the actual import/export/copy flow.
 *
 * Supports both synchronous and asynchronous operations:
 * - Sync callers pass `() => void` and get `void` back.
 * - Async callers pass `() => Promise<void>` and get a `Promise<void>` back.
 */

import { captureError } from '@leagueplatform/observability';

export function withBestEffortReporting(operation: () => void): void;
export function withBestEffortReporting(
  operation: () => Promise<void>,
): Promise<void>;
export function withBestEffortReporting(
  operation: (() => void) | (() => Promise<void>),
): void | Promise<void> {
  let error: unknown;

  const reportError = () => {
    if (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), {
        errorName: 'BulkActionReportingFailure',
      });
    }
  };

  try {
    const result = operation();

    if (result instanceof Promise) {
      return result
        .catch((promiseError: unknown) => {
          error = promiseError;
        })
        .finally(reportError);
    }

    return undefined;
  } catch (syncError: unknown) {
    error = syncError;
    return undefined;
  } finally {
    reportError();
  }
}
